import { useMemo } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import cn from 'classnames';
import { RTableSummaryRow } from '../RTable.props';
import styles from './SummaryRow.module.css';

export interface SummaryRowProps<T> {
  summaryData: RTableSummaryRow;
  columns: ColumnDef<T>[];
  columnOrder: string[];
  className?: string;
}

export default function SummaryRow<T>({
  summaryData,
  columns,
  columnOrder,
  className,
}: SummaryRowProps<T>) {
  // Create a map for quick column lookup
  const columnMap = useMemo(() => {
    const map = new Map<string, ColumnDef<T>>();
    columns.forEach(col => {
      if (col.id) {
        map.set(col.id, col);
      }
    });
    return map;
  }, [columns]);

  return (
    <div className={cn(styles.summaryRow, className)}>
      {columnOrder.map(columnId => {
        const column = columnMap.get(columnId);
        const content = summaryData[columnId];

        if (!column) return null;

        // Get column width and alignment
        const columnSize = column.size || 'auto';
        const columnAlign = (column.meta as any)?.align || 'left';

        return (
          <div
            key={columnId}
            className={cn(styles.summaryCell, {
              [styles.alignLeft]: columnAlign === 'left',
              [styles.alignCenter]: columnAlign === 'center',
              [styles.alignRight]: columnAlign === 'right',
            })}
            style={{
              width: typeof columnSize === 'number' ? `${columnSize}px` : columnSize,
              minWidth: typeof columnSize === 'number' ? `${columnSize}px` : columnSize,
              maxWidth: typeof columnSize === 'number' ? `${columnSize}px` : columnSize,
            }}
          >
            <div className={styles.summaryContent}>{content || ''}</div>
          </div>
        );
      })}
    </div>
  );
}
